import type { Joint } from "@/shared/state/visualization/schema/v1/three-d";

/** Whether a joint can be bound to data, and if not, why not. */
export interface JointControlState {
  bindable: boolean;
  /** Shown beside the joint when it cannot be bound. */
  reason?: string;
}

/**
 * What control, if any, a joint should offer.
 *
 * Follows the extension's own guidance rather than inventing rules, because the
 * failure modes are unobvious: a joint pinned by equal limits looks bindable but
 * cannot move, and a multi-DOF joint looks bindable but cannot be described by
 * one number.
 *
 * A **coupled** joint gets no binding of its own: its value is
 * `multiplier * driver + offset`, evaluated every frame, and offering it a topic
 * would let a user put a parallel gripper into a pose the mechanism forbids. The
 * row says which joint it follows instead, which is the useful thing to know.
 *
 * @param unresolvable the joints `mimicOrder` could not order — those in a
 *   coupling cycle, those following a driver the model does not contain, and
 *   those whose name another joint has taken. None of them has a computable
 *   value, so none of them is worth binding.
 */
export function jointControlState(
  joint: Joint,
  unresolvable?: ReadonlySet<string>,
): JointControlState {
  if (unresolvable?.has(joint.name)) {
    return {
      bindable: false,
      reason: joint.mimic
        ? `Follows ${joint.mimic.joint}, which cannot be resolved`
        : "Another joint of this model has the same name",
    };
  }
  if (joint.mimic) {
    return { bindable: false, reason: `Mimics ${joint.mimic.joint}` };
  }

  switch (joint.type) {
    case "fixed":
      // A rigid attachment. There is nothing to drive.
      return { bindable: false, reason: "Fixed — nothing to drive" };
    case "floating":
      return {
        bindable: false,
        reason: "Floating joints have six degrees of freedom",
      };
    case "planar":
      return {
        bindable: false,
        reason: "Planar joints have three degrees of freedom",
      };
    case "revolute":
    case "continuous":
    case "prismatic":
      // Prismatic is bindable like the two that turn: a joint value is one
      // scalar, and the renderer slides the link by `normalize(axis) · q`
      // instead of turning it by `q`. It was excluded only while a joint had to
      // be expressed through a `Position`, whose three fixed channels cannot
      // state a displacement along an arbitrary axis.
      break;
  }

  if (!joint.axis) {
    // A joint with no axis names no direction to turn about or slide along.
    return { bindable: false, reason: "The model declares no axis" };
  }
  if (isPinned(joint)) {
    // Under URDF 1.0/1.1 `lower`/`upper` default to 0, so a joint declaring only
    // effort and velocity reads as locked. Widening it to ±π would invent travel
    // the robot does not have.
    return { bindable: false, reason: "Pinned by its limits" };
  }
  return { bindable: true };
}

/** Whether the joint's limits leave it no travel at all. */
export function isPinned(joint: Joint): boolean {
  return (
    joint.lower !== undefined &&
    joint.upper !== undefined &&
    joint.lower === joint.upper
  );
}

/**
 * The unit a joint's value is in: the extension's own SI units, which follow the
 * joint's type.
 *
 * The unit is a property of the *joint*, never of the data bound to it — which
 * is why a binding carries a scale rather than a unit string. Anything that
 * reports what a joint holds says it in these.
 */
export function jointUnit(joint: Joint): string {
  return joint.type === "prismatic" ? "m" : "rad";
}

/**
 * The joint's travel, for display, or `undefined` if it declares none.
 *
 * A `continuous` joint turns without end, which is a fact worth showing rather
 * than leaving blank.
 */
export function limitsLabel(joint: Joint): string | undefined {
  if (joint.type === "continuous") {
    return "unbounded";
  }
  if (joint.lower === undefined || joint.upper === undefined) {
    return undefined;
  }
  return `${round(joint.lower)} … ${round(joint.upper)} ${jointUnit(joint)}`;
}

function round(value: number): string {
  return Number.parseFloat(value.toFixed(3)).toString();
}
